import { ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { Header } from '@/components/Header';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { useChrome } from '../context/ChromeContext';
import { ArcheSidebar } from './ArcheSidebar';
import { ExpandedArcheHeader } from './ExpandedArcheHeader';
import { SidebarProvider, useSidebar } from '../context/SidebarContext';
import { SearchResultsProvider } from '../context/SearchResultsContext';

/**
 * Display mode for the ArcheLayout:
 * - 'normal': Full header + sidebar + content (for authenticated users on non-fullscreen arches)
 * - 'public': Minimal header + content, no sidebar (for unauthenticated users on public arches)
 * - 'fullscreen': Minimal header (logo + theme toggle) + content, no sidebar (for full-screen arches)
 * - 'embed': No header at all, just content (for iframe embedding on external sites)
 */
export type ArcheDisplayMode = 'normal' | 'public' | 'fullscreen' | 'embed';

interface ArcheLayoutProps {
  children: ReactNode;
  /**
   * The display mode for the layout.
   * - 'normal': Full header + sidebar + content
   * - 'public': Minimal header + content (no sidebar)
   * - 'fullscreen': Minimal header (logo + theme toggle) + content (no sidebar)
   * - 'embed': No header at all, just content (for iframe embedding)
   */
  displayMode?: ArcheDisplayMode;
  /**
   * Suppress the shared focus-mode "expanded" header (Back-to-Fias + Fias AI +
   * Collapse). An arche opts into this when it renders its own restore affordance
   * in a spot that fits its chrome (e.g. the email arche places it above its
   * account selector at the top-left via `FocusModeRestoreControls`). Without an
   * in-arche replacement, leave this false or the arche becomes un-restorable in
   * focus mode.
   */
  hideExpandedHeader?: boolean;
}

function ArcheLayoutInner({
  children,
  displayMode = 'normal',
  hideExpandedHeader = false,
}: ArcheLayoutProps) {
  const { id: archeId } = useParams<{ id: string }>();
  const { isCollapsed, isMobile, toggle, setCollapsed } = useSidebar();
  const { isHeadersHidden } = useChrome();

  // Embed mode: no header at all, just content (for iframe embedding)
  if (displayMode === 'embed') {
    return <div className="h-screen overflow-auto">{children}</div>;
  }

  // Full-screen mode: no header, no sidebar — content fills the entire viewport
  if (displayMode === 'fullscreen') {
    return <div className="h-screen overflow-auto">{children}</div>;
  }

  // Public mode (not logged in): minimal header, no sidebar
  if (displayMode === 'public') {
    return (
      <div className="flex flex-col h-screen">
        <Header minimal />
        <main className="flex-1 overflow-auto">{children}</main>
      </div>
    );
  }

  // Normal mode: full header + sidebar — unless this arche is in focus mode,
  // in which case the platform Header AND the suggested-arches sidebar are
  // dropped so the arche fills the viewport, and a single thin header replaces
  // them with the way back (leave to Fias, open Fias AI, collapse). State is
  // per-arche (default visible) so other arches are untouched.
  const headersHidden = isHeadersHidden(archeId);
  return (
    <div className="flex flex-col h-screen">
      {!headersHidden && <Header />}
      {/* Expanded (focus mode): the platform Header, the sidebar, and the
          arche's own ArcheHeader are all hidden, so this thin bar is the single,
          consistent way back. Arches with their own layout opt out via
          `hideExpandedHeader` and render their own restore affordance. */}
      {headersHidden && !hideExpandedHeader && archeId && <ExpandedArcheHeader archeId={archeId} />}
      <div className="flex flex-1 overflow-hidden">
        {/* Desktop Sidebar - only render on desktop and when chrome is shown */}
        {!isMobile && !headersHidden && (
          <aside
            className={`
              flex-shrink-0 border-r border-border bg-card overflow-hidden
              transition-all duration-300 ease-in-out
              ${isCollapsed ? 'w-0' : 'w-64'}
            `}
          >
            <div className="w-64 h-full flex flex-col">
              <ArcheSidebar currentArcheId={archeId || ''} onToggle={toggle} />
            </div>
          </aside>
        )}

        {/* Mobile Sheet Sidebar - only on mobile and when chrome is shown (in
            focus mode the toggle that opens it is gone, so don't leave it). */}
        {isMobile && !headersHidden && (
          <Sheet open={!isCollapsed} onOpenChange={(open) => setCollapsed(!open)}>
            <SheetContent side="left" className="w-[280px] p-0 [&>button]:hidden">
              <div className="h-full flex flex-col">
                <ArcheSidebar currentArcheId={archeId || ''} onToggle={toggle} />
              </div>
            </SheetContent>
          </Sheet>
        )}

        {/* Main content */}
        <main className="flex-1 overflow-auto">{children}</main>
      </div>
    </div>
  );
}

export function ArcheLayout({
  children,
  displayMode = 'normal',
  hideExpandedHeader = false,
}: ArcheLayoutProps) {
  return (
    <SidebarProvider>
      <SearchResultsProvider>
        <ArcheLayoutInner displayMode={displayMode} hideExpandedHeader={hideExpandedHeader}>
          {children}
        </ArcheLayoutInner>
      </SearchResultsProvider>
    </SidebarProvider>
  );
}

export default ArcheLayout;
